import * as _ from 'underscore'

import { IUserSession, Dictionary, ToastHelper, LocalizationHelper, ConvertHelper, LogHelper } from '../../../helpers'
import { Enums, Activity } from '../../../models/common'
import { OperatorLayoutSetting } from '../../../models/local'
import { OpDevice, OpLocation, OpDeviceType } from '../../../models/core'
import { OpConsumerNotificationToSend } from '../../../models/custom'
import { IReferenceType } from 'app/models/util/interfaces'
import { DataProviderRequest } from 'app/data-provider'

//#region Public Types

export const SERVICE_NAME: string = 'IMRCashierPanelService'

//#endregion Public Types End

//#region Private Types

type CashierPanelSettings = {
	creditAccordionState: any
	lastSelectedLocationId: number
	measurmentsChartState: any
}

//#endregion Private Types End

export class CashierPanelService {
	private static get LOG_PREFIX(): string {
		return '[SIMAX.Services.CashierPanelService]'
	}

	//#region Ctor

	constructor(private userSession: IUserSession) { }

	//#endregion Ctor End

	//#region Public Methods

	public loadLastConsumerLocationIdFromSettings(): number {
		return this.loadModuleSettings().lastSelectedLocationId
	}

	public saveLastConsumerLocationToSettings(consumerLocationId: number): void {
		const cashierPanelSettings: CashierPanelSettings = this.loadModuleSettings()

		cashierPanelSettings.lastSelectedLocationId = consumerLocationId

		this.saveModuleSettings(cashierPanelSettings)
	}

	public loadCreditAccordionSettings(): any {
		return this.loadModuleSettings().creditAccordionState
	}

	public saveCreditAccordionSettings(creditAccordionState: any): void {
		const cashierPanelSettings: CashierPanelSettings = this.loadModuleSettings()

		cashierPanelSettings.creditAccordionState = creditAccordionState

		this.saveModuleSettings(cashierPanelSettings)
	}

	public loadMeasurmentsChartSettings(): any {
		return this.loadModuleSettings().measurmentsChartState
	}

	public saveMeasurmentsChartSettings(measurmentsChartState: any): void {
		const cashierPanelSettings: CashierPanelSettings = this.loadModuleSettings()

		cashierPanelSettings.measurmentsChartState = measurmentsChartState

		this.saveModuleSettings(cashierPanelSettings)
	}

	public topUpRollback(idConsumer: number, idLocation: number, IdConsumerTransaction: number): JQueryPromise<void> {
		return this.userSession
			.getDataProvider()
			.Consumer.consumerRollbackTopUpTransaction(idConsumer, idLocation, IdConsumerTransaction)
			.then(() => {
				ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.TopUpRollbackDone))
			})
			.fail((error: any) => {
				const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
				ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
				LogHelper.error(CashierPanelService.LOG_PREFIX, `Error during topup rollback operation. ${errorMessage}`)
			})
	}

	public sendNotification(notificationToSendData: OpConsumerNotificationToSend): JQueryPromise<void> {
		return this.userSession
			.getDataProvider()
			.Consumer.sendConsumerNotifications(notificationToSendData)
			.then((result: boolean) => {
				if (result) {
					ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationSent))
				} else {
					ToastHelper.showError(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.NotificationSendFailed))
				}
			})
			.fail((error: any) => {
				const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
				ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
				LogHelper.error(CashierPanelService.LOG_PREFIX, `Unable to send notification. ${errorMessage}`)
			})
	}

	public refreshCredit(idConsumer: number, idLocation: number): JQueryPromise<void> {
		return this.userSession
			.getDataProvider()
			.Consumer.refreshCreditOnDevice(idConsumer, idLocation)
			.then(() => {
				ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CreditRefreshSent))
			})
			.fail((error: any) => {
				const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
				ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
				LogHelper.error(CashierPanelService.LOG_PREFIX, `Unable to send acton to refresh credit. ${errorMessage}`)
			})
	}

	public consumerCommission(idConsumer: number): JQueryPromise<void> {
		return this.userSession
			.getDataProvider()
			.Consumer.commission(idConsumer)
			.then(() => {
				ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.CommissionSent))
			})
			.fail((error: any) => {
				const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
				ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
				LogHelper.error(CashierPanelService.LOG_PREFIX, `Error during consumer commission operation. ${errorMessage}`)
			})
	}

	public consumerDecommission(idConsumer: number, decomissionMode: Enums.DecomissionMode = Enums.DecomissionMode.STANDARD): JQueryPromise<void> {
		return this.userSession
			.getDataProvider()
			.Consumer.decommission(idConsumer, decomissionMode)
			.then(() => {
				ToastHelper.showSuccess(LocalizationHelper.getLocalizedText(LocalizationHelper.Text.DecommissionSent))
			})
			.fail((error: any) => {
				const errorMessage: string = ConvertHelper.getRequestErrorMessage(error)
				ToastHelper.showError(errorMessage || LocalizationHelper.getLocalizedText(LocalizationHelper.Text.ExceptionOccuredPleaseContactAnAdministrator))
				LogHelper.error(CashierPanelService.LOG_PREFIX, `Error during consumer decommission operation. ${errorMessage}`)
			})
	}

	public consumerChangeTariff(idConsumer: number, idNewTariff: number, idOldTariff: number, activationDate: Date): DataProviderRequest<void> {
		return this.userSession.getDataProvider().Consumer.consumerChangeTariff(idConsumer, idNewTariff, idOldTariff, activationDate)
	}

	//#region Checks

	public hasDevicePrepaidAvailable(device: OpDevice): boolean {
		return this.hasDeviceValve(device)
	}

	public hasDeviceValve(device: OpDevice): boolean {
		return device.dataList.getValue(Enums.DataType.HELPER_DEVICE_HAS_VALVE)
	}

	public hasDeviceActivePrepaid(device: OpDevice): boolean {
		if (device == null || device.currentLocations == null || device.idDeviceStateType != Enums.DeviceStateType.ACTIVE || !this.hasDevicePrepaidAvailable(device)) {
			return false
		}

		return true
	}

	public hasLocationOKODevice(location: OpLocation): boolean {
		if (location != null && location.currentDevices != null) {
			if (_.size(location.currentDevices) > 0) {
				return _.some(location.currentDevices, (device: OpDevice) => {
					return this.isOKODevice(device)
				})
			}
		}

		return false
	}

	public hasLocationOLANDevice(location: OpLocation): boolean {
		if (location != null && location.currentDevices != null) {
			return location.currentDevices.some((device: OpDevice) => {
				return this.isOLANDevice(device)
			})
		}

		return false
	}

	public hasRaypayDevice(location: OpLocation): boolean {
		if (location != null && location.currentDevices != null) {
			return location.currentDevices.some((device: OpDevice) => {
				return this.isRayPayDevice(device)
			})
		}

		return false
	}

	public hasLocation_RAYPAY_R0F6_xxx2_Device(location: OpLocation): boolean {
		if (location != null && location.currentDevices != null) {
			return location.currentDevices.some((device: OpDevice) => {
				return device.idDeviceType == Enums.DeviceType.RAYPAY_R0F6_xxx2
			})
		}

		return false
	}

	public isLocationWithActionDevice(location: OpLocation): boolean {
		return location != null && (this.hasLocationOKODevice(location) || this.hasLocationOLANDevice(location) || this.hasRaypayDevice(location))
	}

	public canLoggedOperatorChangeDevicePrepaidTariff(location: OpLocation) {
		return (
			location != null &&
			_.size(location.currentDevices) > 0 &&
			_.some(location.currentDevices, (device: OpDevice) => this.hasDevicePrepaidAvailable(device)) &&
			(this.hasLocationOKODevice(location) || this.hasLocationOLANDevice(location) || this.hasRaypayDevice(location)) &&
			this.hasOperatorPermissionToChangePrepaidTariff()
		)
	}

	public hasOperatorPermissionToChangePrepaidTariff(): boolean {
		return this.userSession.getLoggedOperator().hasPermission(Activity.TARIFF_EDIT) && this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_ALLOW_SEND_PREPAID_ACTION)
	}

	public canOperatorManageValve(): boolean {
		return this.userSession.getLoggedOperator().hasPermission(Activity.CONTROL_VALVE)
	}

	public canOperatorSendDecommission(): boolean {
		return (
			this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_ALLOW_SEND_DECOMMISSION) && this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_ALLOW_SEND_PREPAID_ACTION)
		)
	}

	public canOperatorSendCommission(): boolean {
		return (
			this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_ALLOW_SEND_COMMISSION) && this.userSession.getLoggedOperator().hasPermission(Activity.OPERATOR_ALLOW_SEND_PREPAID_ACTION)
		)
	}

	public isOKODevice(device: OpDevice): boolean {
		if (device != null) {
			const dictionaries: Dictionary<Dictionary<IReferenceType>> = this.userSession.getDataProvider().System.getDictionariesLocal([Enums.ReferenceType.IdDeviceType])

			if (dictionaries != null) {
				const deviceTypes: Dictionary<OpDeviceType> = dictionaries.tryGetTypedValue(Enums.ReferenceType.IdDeviceType)

				if (deviceTypes != null) {
					const deviceType: OpDeviceType = deviceTypes.tryGetValue(device.idDeviceType)
					return deviceType != null && deviceType.idDeviceTypeClass == Enums.DeviceTypeClass.OKO
				}
			}
		}

		return false
	}

	public isOLANDevice(device: OpDevice): boolean {
		return device != null && (device.idDeviceType == Enums.DeviceType.OLAN_5310_C713 || device.idDeviceType == Enums.DeviceType.OLANi5x5_xxD0)
	}

	public isRayPayDevice(device: OpDevice): boolean {
		return device != null && (device.idDeviceType == Enums.DeviceType.RAYPAY_R0F6 || device.idDeviceType == Enums.DeviceType.RAYPAY_R0F6_xxx2 || device.idDeviceType == Enums.DeviceType.RAYPAY_Rx06)
	}

	public isDeviceTariffAvailable(device: OpDevice): boolean {
		return this.isOKODevice(device) || this.isOLANDevice(device) || this.isRayPayDevice(device)
	}

	//#endregion

	//#endregion Public Methods End

	//#region Private Methods

	private loadModuleSettings(): CashierPanelSettings {
		return this.userSession.getOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.CashierPanel), {
			lastSelectedLocationId: null,
			measurmentsChartState: {},
			creditAccordionState: {},
		})
	}

	private saveModuleSettings(settings: CashierPanelSettings) {
		this.userSession.setOperatorLayoutSetting(new OperatorLayoutSetting(Enums.SimaxOperatorLayoutSetting.CashierPanel), settings)
	}

	//#endregion Private Methods End
}
